/**
 * Register / save FCM tokens so drivers get offer alerts (web + native WebView / Capacitor).
 * Native shells often block Web Push / service workers — we register Capacitor Push when available.
 */
import { isRetryable } from '../api/errors';
import { pushDevicesApi } from '../api/pushDevices';
import { notifyDriverNewOffer, handleDriverOfferStopSignal } from './driverOfferRing';
import { readCachedDriverNotifPrefs } from './driverNotificationPrefs';
import { getDriverSession } from './driverSession';
import { pushKind } from './pushKinds';

/**
 * The name of the channel an offer rings on, as Android's settings show it: the
 * name the permissions panel tells a rider to look for.
 *
 * Since ADR 0013 the offer's channels belong to the ring core (`armkit/android/ring`),
 * created natively from `IngoRingConfig.java`, which retires the old
 * `ingo_driver_offers`. The JavaScript creates no offer channel and names no
 * channel id, so there is no second copy of an id to keep equal. This name must
 * equal `IngoRingConfig.CALL_CHANNEL_NAME`; `driverPushChannel.test.js` reads the
 * Java file and fails if they differ.
 */
export const OFFER_CHANNEL_NAME = 'Incoming jobs';

const SILENT = () => {};
/** Set by driverPushBootstrap so a cold-start tap is not lost before React mounts. */
let onOfferTapped = SILENT;

/**
 * Install the sink that receives notification taps.
 * @param {(detail: { link: string, offerKey: string, tag: string }) => void} fn
 */
export function setOfferTapHandler(fn) {
  onOfferTapped = typeof fn === 'function' ? fn : SILENT;
}

let capacitorListenersReady = false;
let channelReady = false;

function isNativeShell() {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === 'function') return Boolean(cap.isNativePlatform());
    return Boolean(cap.isNative || cap.platform === 'ios' || cap.platform === 'android');
  } catch {
    return false;
  }
}

function nativePlatformLabel() {
  try {
    const cap = window.Capacitor;
    const p = typeof cap?.getPlatform === 'function' ? cap.getPlatform() : cap?.platform;
    if (p === 'ios' || p === 'android') return p;
  } catch {
    /* ignore */
  }
  return 'android';
}

/**
 * @returns {Promise<any | null>} Capacitor PushNotifications plugin, or null
 */
async function getCapacitorPushPlugin() {
  if (typeof window === 'undefined') return null;
  // On the web the plugin is Capacitor's proxy with no implementation behind
  // it. The proxy answers every property, `then` included, so returning it
  // from this async function makes the Promise call `then`, which rejects
  // "not implemented on web" unhandled, and the caller's await never settles.
  // Native keeps its path, proven on a device.
  try {
    if (!isNativeShell() && !window.Capacitor?.isPluginAvailable?.('PushNotifications')) return null;
  } catch {
    return null;
  }
  try {
    const injected = window.Capacitor?.Plugins?.PushNotifications;
    if (injected && typeof injected.register === 'function') return injected;
  } catch {
    /* ignore */
  }
  try {
    const mod = await import('@capacitor/push-notifications');
    if (mod?.PushNotifications) return mod.PushNotifications;
  } catch {
    /* package missing or not in Capacitor */
  }
  return null;
}

/**
 * Withdraw a delivered system notification on native. No-op on web (the service
 * worker handles that path) and when the plugin is absent. Never throws.
 *
 * With a tag, removes that one offer. Without one, removes every `ingo-offer-*`
 * notification — the "clear everything" case when the app cannot tell which.
 * @param {string} [tag]
 */
export async function removeDeliveredOfferNotification(tag) {
  const Push = await getCapacitorPushPlugin();
  if (!Push || typeof Push.getDeliveredNotifications !== 'function') return;
  try {
    const { notifications = [] } = (await Push.getDeliveredNotifications()) || {};
    const want = String(tag || '').trim();
    const matching = notifications.filter((n) => {
      const t = String(n?.tag || '');
      return want ? t === want : t.startsWith('ingo-offer-');
    });
    console.info(
      `[driverPush] withdraw offer notification ${JSON.stringify({
        tag: want || '(all ingo-offer-*)',
        delivered: notifications.length,
        matching: matching.length,
      })}`,
    );
    if (!matching.length) return;
    await Push.removeDeliveredNotifications({ notifications: matching });
  } catch (e) {
    console.warn(`[driverPush] withdraw failed: ${e?.message || e}`);
  }
}

/**
 * This phone's token, to InGo's API (`rider/me/push-devices`). The rider is
 * the session's: `driverId` only says one is signed in. Every rider signs in
 * on the API since M2, so Supabase's `driver_push_tokens` is no longer
 * written: InGo's server is the one that rings (InGo `Modules/Push`).
 *
 * @param {string} driverId
 * @param {string} token
 * @param {string} platform
 */
async function upsertPushToken(driverId, token, platform) {
  if (!readCachedDriverNotifPrefs(driverId).push_when_closed) {
    // The rider turned push off on this phone: it stays off InGo's list.
    console.info('[driverPush] push when closed is off on this phone: not registered');
    return { ok: false, error: 'Push is turned off in Notifications' };
  }
  try {
    const { rings } = await pushDevicesApi.register(token, platform || 'web');
    rememberToken(token);
    // Serialised into the string: a WebView logs a trailing object as "[object Object]".
    console.info(`[driverPush] token registered ${JSON.stringify({ platform: platform || 'web', rings })}`);
    return { ok: true };
  } catch (e) {
    console.warn(`[driverPush] token registration failed ${JSON.stringify({ code: e?.code || '', status: e?.status || 0, message: e?.message || String(e) })}`);
    // The API unreachable or failing may answer later; a refusal will not.
    return { ok: false, error: e?.message || String(e), retryable: isRetryable(e) };
  }
}

function handleIncomingOfferPayload(payload) {
  const data = payload?.data || {};
  const type = String(data.type || payload?.type || '').toLowerCase();

  // Log BEFORE every guard below. Otherwise "the handler never ran" and "it ran and
  // decided to do nothing" both leave no trace, and they need opposite fixes.
  // Serialised into the message string on purpose: Capacitor's console bridge
  // logs a trailing object argument to logcat as "[object Object]", which loses
  // both the values and the prefix that makes the line greppable on a device.
  console.info(
    `[driverPush] payload received ${JSON.stringify({
      type: type || '(none)',
      tag: data.tag || '',
      offerKey: data.offerKey || data.offer_key || '',
      hasNotificationBlock: Boolean(payload?.notification),
    })}`,
  );
  const kind = pushKind(data, payload);
  if (kind === 'test') {
    // The office's test notification with the app open: Android shows nothing,
    // and it must never ring as an offer (`pushKinds.js`).
    console.info('[driverPush] test notification received with the app open');
    return;
  }
  if (kind === 'delivery_update') {
    // A client's delivery update with the app open: Android shows nothing
    // (a notification message in the foreground), and it is never an offer.
    // The screens that show deliveries read again (`clientPush.js`).
    try {
      window.dispatchEvent(new CustomEvent('ingo-client-update', { detail: { deliveryId: String(data.deliveryId || ''), status: String(data.status || '') } }));
    } catch {
      /* ignore */
    }
    return;
  }
  if (kind === 'stop') {
    const tag = data.tag || payload?.tag || '';
    const offerKey = data.offerKey || data.offer_key || '';
    handleDriverOfferStopSignal(offerKey, tag);
    // Withdraw the system notification too. The web service worker does this via
    // closeNotificationsByTag; on native nothing did, so a driver whose offer was
    // taken by someone else kept a stale "New InGo delivery" in the shade.
    // Measured 2026-09-02: the stop payload reached the backgrounded app and this
    // handler ran, but the notification stayed posted. One order, one
    // notification — and it must go when the order does.
    void removeDeliveredOfferNotification(tag);
    try {
      window.dispatchEvent(
        new CustomEvent('ingo-driver-offer-stop', {
          detail: { title: '', body: '', tag, offerKey },
        }),
      );
    } catch {
      /* ignore */
    }
    return;
  }

  const driverId = getDriverSession()?.id;
  const prefs = readCachedDriverNotifPrefs(driverId);
  if (!prefs.new_offers) {
    console.info(
      `[driverPush] offer suppressed by driver preference ${JSON.stringify({
        driverId: driverId ? 'present' : 'none',
      })}`,
    );
    return;
  }
  const title = payload?.notification?.title || data.title || payload?.title || 'New InGo booking';
  const body =
    payload?.notification?.body || data.body || payload?.body || 'Open the app to accept or reject.';
  const tag = data.tag || payload?.tag || `ingo-offer-${Date.now()}`;
  const offerKey = String(data.offerKey || data.offer_key || tag.replace(/^ingo-offer-/, '') || '').trim();
  notifyDriverNewOffer({
    title,
    body,
    tag,
    offerKey,
    onClickPath: data.link || payload?.link || '/driver/home',
    sound: prefs.offer_sound,
    banner: true,
    loop: true,
  });
  try {
    window.dispatchEvent(
      new CustomEvent('ingo-driver-push-offer', {
        detail: { title, body, tag, offerKey },
      }),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Play in-app ring when a push arrives while the app is open.
 */
export async function startDriverPushForegroundListener() {

  const Push = await getCapacitorPushPlugin();
  if (Push && !capacitorListenersReady) {
    capacitorListenersReady = true;
    try {
      await Push.addListener('pushNotificationReceived', (notification) => {
        handleIncomingOfferPayload({
          title: notification?.title,
          body: notification?.body,
          data: notification?.data || {},
        });
      });
      await Push.addListener('pushNotificationActionPerformed', (action) => {
        const data = action?.notification?.data || {};
        if (String(data.type || '') === 'delivery_update') {
          // A client's update opens its delivery, parked first: on a cold
          // start the router does not exist yet (`clientPush.js`).
          void import('./clientPush').then((m) => m.openClientUpdate(data)).catch(() => {});
          return;
        }
        const link = data.link || '/driver/home';
        const detail = {
          link,
          offerKey: String(data.offerKey || data.offer_key || '').trim(),
          tag: String(data.tag || '').trim(),
          // 'accept' | 'decline' when a notification button was pressed (set by
          // OfferMessagingService on Android); '' for a plain tap on the banner.
          action: String(data.ingoAction || '').trim().toLowerCase(),
        };
        console.info(`[driverPush] offer tapped ${JSON.stringify(detail)}`);

        // Hand to the bootstrap sink first. On a cold start the OS launches the app
        // *into* this event, so the router does not exist yet and assigning
        // window.location would reload the app we are already starting.
        try {
          onOfferTapped(detail);
        } catch (e) {
          console.warn('[driverPush] tap sink threw', e?.message || e);
        }
      });
    } catch (e) {
      console.warn('[driverPush] Capacitor listeners failed', e);
    }
  }
}

/**
 * @param {string} driverId
 * @returns {Promise<{ ok: boolean, token?: string, error?: string, permission?: string, platform?: string }>}
 */
/**
 * Asks the ring core to create its channels now (ADR 0013), so a rider finds
 * "Incoming jobs" in the app's notification settings before the first ring and
 * an old phone's `ingo_driver_offers` is retired. The JavaScript itself creates
 * no offer channel: one creator, so no two copies of a channel to disagree
 * (Capacitor's `sound: 'default'` once made a silent channel this way).
 * No-op on the web. Safe to call repeatedly.
 * @param {{ ensureOfferChannels: () => Promise<any> } | null} [native] the core's bridge; tests pass a fake
 */
export async function ensureOfferChannel(native) {
  if (channelReady) return;
  const core = native || ringChannelsBridge();
  if (typeof core?.ensureOfferChannels !== 'function') return;
  try {
    const r = await core.ensureOfferChannels();
    channelReady = true;
    console.info(`[driverPush] ring channels ready ${JSON.stringify({ id: r?.id || '', headsUp: r?.headsUp })}`);
  } catch (e) {
    // Never fatal: the core creates its channels again as it rings.
    console.warn(`[driverPush] ring channels not created ${JSON.stringify({ message: e?.message || String(e) })}`);
  }
}

/**
 * The native call, through the bridge the shell already put on `window.Capacitor`.
 * This module never imports `@capacitor/core`, on purpose: importing it replaces
 * `window.Capacitor` with its own object, which is what the rest of this file reads
 * (`isNativeShell`). Native only; null on the web.
 */
function ringChannelsBridge() {
  if (!isNativeShell()) return null;
  const cap = window.Capacitor;
  const plugin = cap?.Plugins?.IngoPermissions;
  if (plugin && typeof plugin.ensureOfferChannels === 'function') return plugin;
  if (typeof cap?.nativePromise === 'function') {
    return { ensureOfferChannels: () => cap.nativePromise('IngoPermissions', 'ensureOfferChannels', {}) };
  }
  return null;
}

/**
 * This phone's native push token: the permission asked for when it is not
 * granted yet, then FCM's token. Shared by the rider's registration (offers)
 * and the client's (`clientPush.js`, delivery updates); what the token is
 * registered for is the caller's.
 *
 * @returns {Promise<{ ok: boolean, token?: string, platform?: string, permission?: string, error?: string }>}
 */
export async function nativePushToken() {
  const Push = await getCapacitorPushPlugin();
  if (!Push) return { ok: false, error: 'Native push plugin unavailable', permission: 'unsupported' };

  // Once permission is granted, a failure is Google's or the network's, not the
  // person's answer: it may succeed later (pushRetry.js), and it is no "denied".
  let granted = false;
  try {
    let perm = await Push.checkPermissions();
    if (perm.receive !== 'granted') {
      perm = await Push.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      return { ok: false, permission: perm.receive || 'denied', error: 'Notification permission not granted' };
    }
    granted = true;

    const tokenPromise = new Promise((resolve, reject) => {
      let settled = false;
      /** @type {Array<{ remove: () => Promise<void> }>} */
      const handles = [];
      const timer = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          void Promise.all(handles.map((h) => Promise.resolve(h?.remove?.()).catch(() => {})));
          reject(new Error('Native push registration timed out'));
        }
      }, 15000);

      const finish = (fn) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        void Promise.all(handles.map((h) => Promise.resolve(h?.remove?.()).catch(() => {})));
        fn();
      };

      // Promise.resolve() on purpose. `getCapacitorPushPlugin()` prefers the
      // bridge-injected `window.Capacitor.Plugins.PushNotifications`, and on
      // Capacitor 8 that object's addListener returns a plain handle, not a
      // Promise — only the ESM-imported plugin returns a Promise. Calling
      // `.then()` on the injected one throws
      // "t.addListener(...).then is not a function", the whole registration is
      // caught as a failure, and no token is ever obtained. Observed on device
      // 2026-09-02. This works with either shape.
      void Promise.resolve(
        Push.addListener('registration', (t) => {
          finish(() => resolve(String(t?.value || '').trim()));
        }),
      ).then((h) => h && handles.push(h));

      void Promise.resolve(
        Push.addListener('registrationError', (err) => {
          finish(() => reject(new Error(err?.error || 'Native push registration failed')));
        }),
      ).then((h) => h && handles.push(h));

      void Push.register().catch((e) => finish(() => reject(e)));
    });

    const token = await tokenPromise;
    if (!token) return { ok: false, permission: 'granted', error: 'Empty native push token' };

    return { ok: true, permission: 'granted', token, platform: nativePlatformLabel() };
  } catch (e) {
    return { ok: false, error: e?.message || String(e), permission: granted ? 'granted' : 'denied', retryable: granted };
  }
}

async function registerNativePushToken(driverId) {
  const Push = await getCapacitorPushPlugin();
  if (!Push) return { ok: false, error: 'Native push plugin unavailable', permission: 'unsupported' };

  // Not awaited: the token is what gets this phone rung, and a channel is the core's to
  // create (it does so again as it rings). A native call that never answered must not
  // hold the registration up (ADR 0013).
  void ensureOfferChannel();

  const native = await nativePushToken();
  if (!native.ok) return native;

  const up = await upsertPushToken(driverId, native.token, native.platform);
  if (!up.ok) return { ok: false, permission: 'granted', token: native.token, error: up.error, platform: native.platform, retryable: Boolean(up.retryable) };

  void startDriverPushForegroundListener();
  return native;
}

/**
 * Request notification permission (if needed), get FCM / native token, upsert for this driver.
 * @param {string} driverId
 * @returns {Promise<{ ok: boolean, token?: string, error?: string, permission?: string, platform?: string }>}
 */
export async function registerDriverPushToken(driverId) {
  const id = String(driverId || '').trim();
  if (!id) return { ok: false, error: 'Missing driver id' };

  // Prefer native push inside Capacitor / WebView apps (Web Push usually fails there).
  if (isNativeShell() || (await getCapacitorPushPlugin())) {
    const native = await registerNativePushToken(id);
    if (native.ok) return native;
    // Fall through to web if native failed and web might still work.
    if (isNativeShell()) return native;
  }

  // A browser: no web push since Firebase's JavaScript left the app (2 Oct 2026,
  // Rio: the app off Supabase and Firebase). Offers reach a rider in a browser
  // while the page is open; the Android app is rung by InGo's server.
  return { ok: false, error: 'Push reaches the Android app; in a browser, offers show while the page is open.', permission: 'unsupported' };
}

const TOKEN_KEY = 'ingo_push_token';

/** The token this phone last registered, so it can be taken off again. */
function rememberToken(token) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Private mode: forgetting then needs the next sign-out instead.
  }
}

/**
 * Takes this phone off InGo's push list (the rider turned "push when closed"
 * off). True when the server was told; false when there was nothing to forget.
 */
export async function forgetThisPhone() {
  let token = '';
  try {
    token = localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    token = '';
  }
  if (!token) {
    console.info('[driverPush] nothing to forget: this phone registered no token');
    return false;
  }
  await pushDevicesApi.forget(token);
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
  console.info('[driverPush] this phone is off the push list');
  return true;
}

/**
 * On sign-out. Nothing to send: InGo's server forgets this phone with the
 * session it belonged to (the session's token is deleted on sign-out, and the
 * phone's row with it), so a shared handset stops ringing for this rider
 * whether or not this runs, or runs before the token is cleared. Kept, and
 * logged, so "signed out" and "never asked" stay two different lines.
 *
 * @param {string | null | undefined} driverId
 */
export async function clearDriverPushToken(driverId) {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
  console.info(`[driverPush] signed out; the server forgets this phone with the session ${JSON.stringify({ driverId: driverId ? 'present' : 'none' })}`);
}

export { isNativeShell, getCapacitorPushPlugin };
